import { defineStore, getActivePinia, type Pinia, type StoreGeneric } from "pinia";
import i18n from "@/app/plugins/i18n";
import moment from "moment-timezone";
import axios from "@/app/plugins/axios";
import * as Sentry from "@sentry/vue";
import { LoginUser, AuthState, FirebaseUser } from "@/auth/types/login";
import {
  applyActionCode,
  getMultiFactorResolver,
  multiFactor,
  TotpMultiFactorGenerator,
  type User,
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  sendEmailVerification,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
  IdTokenResult,
  MultiFactorError,
  UserCredential,
} from "firebase/auth";
import router from "@/app/router";
import { AxiosError, AxiosResponse } from "axios";
import { ChangeSessionPayload, CreateUser, CreateAccount } from "@/auth/types/register";
import { Navigator as CustomNavigator } from "@/shared/types/global";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import { useAccountStore } from "@/account/stores/account";
import { db } from "@/app/plugins/firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { NectarStatus, AccountPlan } from "@/shared/enums/status.enum";
import {
  setCurrentAccount,
  removeCurrentAccount,
  updateCurrentAccount,
} from "@/shared/utils/sessionStorage.utils";
import { isFirebaseAuthNetworkRequestFailed } from "@/shared/utils/firebaseAuthError.utils";
import { getErrorMessage } from "@/shared/utils/errorMessage";
import { isMerchantSessionRoute, redirectMigratedAccount } from "@/auth/services/accountMigration";

let activeUserUnsubscribe: (() => void) | null = null;
let activeAccountSecurityUnsubscribe: (() => void) | null = null;
let activeAccountPlanUnsubscribe: (() => void) | null = null;
let accountSessionVersion = 0;
let sentryAuthAttributeKeys: string[] = [];

const STANDARD_FIREBASE_TOKEN_CLAIMS = new Set([
  "aud",
  "auth_time",
  "email",
  "email_verified",
  "exp",
  "firebase",
  "iat",
  "iss",
  "name",
  "phone_number",
  "picture",
  "sub",
  "user_id",
]);

function getSentryAttributeValue(value: unknown): string | number | boolean {
  if (["string", "number", "boolean"].includes(typeof value)) {
    return value as string | number | boolean;
  }

  if (value === null || value === undefined) {
    return "";
  }

  try {
    return JSON.stringify(value).slice(0, 2000);
  } catch {
    return String(value);
  }
}

function getCustomFirebaseClaimAttributes(tokenResult?: IdTokenResult) {
  return Object.entries(tokenResult?.claims || {}).reduce<
    Record<string, string | number | boolean>
  >((attributes, [claimKey, claimValue]) => {
    if (!STANDARD_FIREBASE_TOKEN_CLAIMS.has(claimKey)) {
      attributes[`firebase.claim.${claimKey}`] = getSentryAttributeValue(claimValue);
    }

    return attributes;
  }, {});
}

function getAuthMeResponseSummary(response?: AxiosResponse) {
  const data = response?.data;
  const userAccounts = data?.user?.accounts;
  const currentAccount = data?.currentAccount;

  return {
    status: response?.status,
    statusText: response?.statusText,
    hasData: data !== undefined && data !== null,
    dataType: data === null ? "null" : Array.isArray(data) ? "array" : typeof data,
    dataKeys: data && typeof data === "object" ? Object.keys(data) : [],
    hasUser: !!data?.user,
    userId: data?.user?.id ?? data?.user?.uid,
    hasUserAccounts: !!userAccounts,
    userAccountIds: userAccounts ? Object.keys(userAccounts) : [],
    userAccountCount: userAccounts ? Object.keys(userAccounts).length : 0,
    hasCurrentAccount: !!currentAccount,
    currentAccountId: currentAccount?.id,
    currentAccountPlan: currentAccount?.accountPlan,
    currentAccountStatus: currentAccount?.nectarStatus,
    nextAccountId: data?.nextAccountId,
    redirectToRegister: data?.redirectToRegister,
  };
}

function getAuthApiErrorData(error: unknown): unknown {
  const apiError = error as {
    response?: { data?: unknown };
    data?: unknown;
    message?: string;
  } | null;
  return apiError?.response?.data ?? apiError?.data ?? apiError?.message ?? null;
}

export interface ProcessTokenResult {
  result?: boolean;
  accountId?: string;
}

interface FirebaseAccountSubscription {
  productName?: string;
  status?: string;
  current_period_end?: number;
}

interface FirebaseAccountSnapshot {
  migratedToBeta?: boolean;
  settings?: {
    security?: {
      require2fa?: boolean;
    };
  };
  subscription?: FirebaseAccountSubscription;
}

function setSentryAuthContext(params: {
  userId: string;
  currentAccountId: string;
  role?: string;
  tokenResult?: IdTokenResult;
}) {
  const scope = Sentry.getGlobalScope();

  sentryAuthAttributeKeys.forEach((key) => scope.removeAttribute(key));

  const attributes = {
    "auth.logged_in": true,
    "auth.user_id": params.userId,
    "auth.current_account_id": params.currentAccountId,
    "auth.role": params.role || "",
    ...getCustomFirebaseClaimAttributes(params.tokenResult),
  };

  sentryAuthAttributeKeys = Object.keys(attributes);
  scope.setAttributes(attributes);
  Sentry.setUser({ id: params.userId });
}

function clearSentryAuthContext() {
  const scope = Sentry.getGlobalScope();

  sentryAuthAttributeKeys.forEach((key) => scope.removeAttribute(key));
  sentryAuthAttributeKeys = [];
  Sentry.setUser(null);
}

function handleFirebaseAuthNetworkError(error: unknown, stage: string): boolean {
  if (!isFirebaseAuthNetworkRequestFailed(error)) {
    return false;
  }

  Sentry.addBreadcrumb({
    category: "auth",
    message: "Firebase Auth network request failed",
    level: "warning",
    data: { stage },
  });
  useSnackBarStore().toastError({
    message: i18n.global.t("errors.networkRequestFailed"),
  });

  return true;
}

async function refreshCurrentFirebaseUser(stage: string): Promise<boolean> {
  const auth = getAuth();
  if (!auth.currentUser) {
    return false;
  }

  try {
    await auth.currentUser.reload();
    await auth.currentUser.getIdToken(true);
    return true;
  } catch (error) {
    if (handleFirebaseAuthNetworkError(error, stage)) {
      return false;
    }

    throw error;
  }
}

export const useAuthStore = defineStore("auth", {
  state: (): AuthState => ({
    user: null,
    userId: "",
    authUser: null,
    loading: false,
    logoutLoading: false,
    changeSessionLoading: false,
    pendingAccountId: null,
    verified: false,
    realTimeFavReaders: {},
    accountStatus: "",
    accountPlan: AccountPlan.STARTER,
    accountTimezone: "",
    isTestEnv: false,
    security: null,
    mfaResolver: null,
  }),
  getters: {
    isLoading: (state) => state.loading,
    isVerified: (state) => state.verified,
    getUserId: (state) => {
      return state.userId;
    },
    getCustomer: (state) => {
      try {
        const customAttributesJson = (
          state.authUser as { reloadUserInfo?: { customAttributes?: string } } | null
        )?.reloadUserInfo?.customAttributes;
        const customAttributes = JSON.parse(customAttributesJson ?? "null") as {
          customer?: string;
        } | null;
        const customer = customAttributes?.customer;
        return customer ?? null;
      } catch {
        return null;
      }
    },
    getAuthInitials: (state) => {
      const authName = state.authUser?.displayName || "";

      const getInitials = (name: string) => {
        const parts = name.split(" ") ?? [];
        let initials;

        if (parts.length > 1) {
          const first = (parts.shift() ?? "").charAt(0);
          const last = (parts.pop() ?? "").charAt(0);
          initials = `${first}${last}`.trim();
        } else {
          initials = name.substring(0, 2);
        }

        return initials.toUpperCase();
      };

      return getInitials(authName);
    },
    getRoleName: (state): string => {
      return state.user?.accounts
        ? state.user?.accounts[useAccountStore().account?.id || ""]?.role
        : "staff";
    },
    getAuthDisplayName: (state) => state.authUser?.displayName,
    getAuthEmail: (state) => state.authUser?.email,
    isLoggedIn: (state): boolean => {
      return !!state.user && !!state.authUser && Object.keys(state.user?.accounts ?? {}).length > 0;
    },
    isStaff: (state): boolean => {
      const roleName = state.user?.accounts
        ? state.user?.accounts[useAccountStore().account?.id || ""]?.role
        : "staff";
      return roleName === "staff";
    },
    isOwner: (state): boolean => {
      const roleName = state.user?.accounts
        ? state.user?.accounts[useAccountStore().account?.id || ""]?.role
        : "staff";
      return roleName === "owner";
    },
    getUserAccounts: (state) => {
      return state.user?.accounts || {};
    },
    getAccountPlan: (state) => {
      return state.accountPlan;
    },
    getAccountTimezone: (state) => {
      return state.accountTimezone;
    },
    anyAccountRequires2fa: (state): boolean => {
      return state.security?.anyAccountRequires2fa === true;
    },
    isMfaEnrolled: (): boolean => {
      const { currentUser } = getAuth();
      return !!currentUser && multiFactor(currentUser).enrolledFactors.length > 0;
    },
  },
  actions: {
    async processToken({ token }: { token: string }): Promise<ProcessTokenResult | false> {
      return axios
        .post(`/token/${token}/process`)
        .then((response: AxiosResponse) => {
          return (response.data || { result: true }) as ProcessTokenResult;
        })
        .catch((error) => {
          useSnackBarStore().toastError({ message: getErrorMessage(error) });

          return false;
        });
    },
    async fetchTokenInfo({ token }: { token: string }): Promise<AxiosResponse> {
      return axios.get(`/publicApi/token/${token}/info`);
    },
    needToVerifyEmail(): boolean {
      const auth = getAuth();

      return !!auth.currentUser && !auth.currentUser.emailVerified;
    },
    async logout(): Promise<void> {
      this.logoutLoading = true;
      accountSessionVersion += 1;
      activeAccountPlanUnsubscribe?.();
      activeAccountPlanUnsubscribe = null;
      if (activeUserUnsubscribe) {
        activeUserUnsubscribe();
        activeUserUnsubscribe = null;
      }
      if (activeAccountSecurityUnsubscribe) {
        activeAccountSecurityUnsubscribe();
        activeAccountSecurityUnsubscribe = null;
      }
      clearSentryAuthContext();
      this.authUser = null;
      this.user = null;
      useAccountStore().setCurrentAccount(null);
      await signOut(getAuth());
      await router.push({ name: "login" });
      removeCurrentAccount();
      // clear all stores before leave
      const activePinia = getActivePinia() as (Pinia & { _s: Map<string, StoreGeneric> }) | null;
      activePinia?._s.forEach((store) => store.$reset());
      this.logoutLoading = false;
    },

    async login(payload: LoginUser): Promise<void> {
      this.loading = true;
      const { email, password, token, persist } = payload;
      const auth = getAuth();

      if (persist) {
        await setPersistence(auth, browserLocalPersistence);
      }
      const user = await signInWithEmailAndPassword(auth, email, password)
        .then((userCredentials: UserCredential) => {
          return userCredentials.user as User | undefined;
        })
        .catch((error: unknown) => {
          this.loading = false;
          if (handleFirebaseAuthNetworkError(error, "login")) {
            return undefined;
          }
          const authError = error as { code?: string; message?: string } | null;
          if (authError?.code === "auth/multi-factor-auth-required") {
            // The user has a TOTP factor enrolled; ask for the code.
            this.mfaResolver = getMultiFactorResolver(auth, error as MultiFactorError);
            return undefined;
          }
          if (authError?.message?.includes("wrong-password")) {
            useSnackBarStore().toastError({
              message: i18n.global.t("page.login.wrongPassword"),
            });
          } else if (authError?.message?.includes("user-not-found")) {
            useSnackBarStore().toastError({
              message: i18n.global.t("page.login.couldNotFindAccount"),
            });
          } else {
            useSnackBarStore().toastError({
              message: authError?.message,
            });
          }
          return undefined;
        });

      await this.finishEmailLogin(user, token);
    },

    async completeMfaLogin(code: string, token?: string): Promise<void> {
      const resolver = this.mfaResolver;
      if (!resolver) {
        return;
      }
      const totpHint = resolver.hints.find(
        (hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID,
      );
      if (!totpHint) {
        useSnackBarStore().toastError({
          message: i18n.global.t("page.login.mfa.unsupportedFactor"),
        });
        return;
      }
      this.loading = true;
      const user = await resolver
        .resolveSignIn(TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, code.trim()))
        .then((userCredentials: UserCredential) => {
          this.mfaResolver = null;
          return userCredentials.user as User | undefined;
        })
        .catch((error: unknown) => {
          this.loading = false;
          if (handleFirebaseAuthNetworkError(error, "mfa login")) {
            return undefined;
          }
          useSnackBarStore().toastError({
            message: i18n.global.t("page.login.mfa.invalidCode"),
          });
          const authError = error as { code?: string; message?: string } | null;
          Sentry.addBreadcrumb({
            category: "auth.mfa",
            message: `MFA sign-in failed: ${authError?.code || authError?.message}`,
            level: "warning",
          });
          return undefined;
        });

      await this.finishEmailLogin(user, token);
    },

    async finishEmailLogin(user: User | undefined, token?: string): Promise<void> {
      if (user && user.emailVerified) {
        useSnackBarStore().toastSuccess({
          message: i18n.global.t("page.login.success"),
        });
        const tokenResult = token ? await this.processToken({ token }) : null;
        if (token && !tokenResult) {
          this.loading = false;
          return;
        }
        if (tokenResult && tokenResult.accountId) {
          const refreshed = await refreshCurrentFirebaseUser("login token session refresh");
          if (!refreshed) {
            this.loading = false;
            return;
          }
          await this.changeSession({ accountId: tokenResult.accountId });
          this.loading = false;
          return;
        }
        await this.afterLogin();
        this.loading = false;
      } else if (user && !user.emailVerified) {
        sendEmailVerification(user);
        useSnackBarStore().toastSuccess({
          message: i18n.global.t("page.login.emailVerificationSent"),
        });
        this.loading = false;
      }
    },

    loginWithGoogle(): void {
      const provider = new GoogleAuthProvider();

      signInWithPopup(getAuth(), provider)
        .then(async () => {
          this.loading = true;
          const tokenQuery = router.currentRoute.value.query?.token;
          const token = Array.isArray(tokenQuery) ? (tokenQuery[0] ?? undefined) : tokenQuery;
          const tokenResult = token ? await this.processToken({ token }) : null;
          if (token && !tokenResult) {
            this.loading = false;
            return;
          }
          if (tokenResult && tokenResult.accountId) {
            const refreshed = await refreshCurrentFirebaseUser(
              "google login token session refresh",
            );
            if (!refreshed) {
              this.loading = false;
              return;
            }
            await this.changeSession({ accountId: tokenResult.accountId });
            this.loading = false;
            return;
          }
          await this.fetchAndSetUser();

          // The user exist and is ok for login
          if (this.user?.accounts) {
            useSnackBarStore().toastSuccess({
              message: i18n.global.t("page.login.success"),
            });
            await this.afterLogin();
            this.loading = false;
          } else {
            // The firestore user not exist, create one as owner and ask to create the account
            await this.updateUser();
            await this.fetchAndSetUser();
          }
        })
        .catch((error: unknown) => {
          this.loading = false;
          if (handleFirebaseAuthNetworkError(error, "google login")) {
            return;
          }
          const authError = error as { code?: string } | null;
          if (authError?.code === "auth/multi-factor-auth-required") {
            this.mfaResolver = getMultiFactorResolver(getAuth(), error as MultiFactorError);
            return;
          }

          Sentry.captureException(error, {
            tags: {
              area: "auth",
              action: "googleLogin",
            },
          });
        });
    },

    async afterLogin(options: { forceTokenRefresh?: boolean } = {}): Promise<void> {
      if (router.currentRoute.value.matched.some((record) => record.meta.skipMerchantGuards)) {
        return;
      }
      const fetchedUser = await this.fetchAndSetUser(options);
      if (!fetchedUser) {
        return;
      }
      this.authUser = getAuth().currentUser;
      const currentAccount = useAccountStore().account;
      this.accountStatus = currentAccount?.nectarStatus;

      if (["invite", "resetpassword"].includes(String(router.currentRoute.value.name))) {
        return;
      }
      // Handle account status-based routing here because status guard running before this method, status is not updated
      const hasMissingAccountStatus = !this.accountStatus?.trim();
      if (
        router.currentRoute.value.name === "accountSetup" ||
        (router.currentRoute.value.name === "login" &&
          (this.accountStatus === NectarStatus.ACTIVE || hasMissingAccountStatus) &&
          this.isLoggedIn)
      ) {
        router.push({ name: "dashboard" });
        return;
      } else if (this.accountStatus === NectarStatus.NEW) {
        router.push({ name: "accountSetup" });
        return;
      }

      if (this.user?.accounts && Object.keys(this.user.accounts).length === 0) {
        router.push({ name: "register", query: { step: 3 } });
        return;
      }

      //other routing scenarios
      if (!this.isLoggedIn) {
        router.push({ name: "login" });
        return;
      }

      if (this.needToVerifyEmail() || !this.user) {
        router.push({
          name: "register",
          query: router.currentRoute.value.query,
        });
        return;
      }
      //default routing
      const redirectedFrom = router.currentRoute.value.redirectedFrom;
      const isNamedRedirect = !!redirectedFrom?.name;
      const redirectName =
        (isNamedRedirect ? redirectedFrom?.name : undefined) ||
        router.currentRoute.value.name?.toString().replace("register", "dashboard") ||
        "dashboard";

      router.push({
        name: redirectName,
        query: isNamedRedirect ? redirectedFrom?.query : undefined,
        params: isNamedRedirect ? redirectedFrom?.params : undefined,
      });
    },

    setUser(userData: FirebaseUser | null) {
      this.user = userData;
    },

    setAccountPlan(accountPlan: string) {
      this.accountPlan = accountPlan;
    },

    setAccountTimezone(accountTimezone: string) {
      this.accountTimezone = accountTimezone;
    },

    async fetchAndSetUser(options: { forceTokenRefresh?: boolean } = {}): Promise<boolean> {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      const sessionVersion = accountSessionVersion;
      if (!currentUser) {
        return false;
      }

      const doFetchMe = async (): Promise<boolean> => {
        this.loading = true;
        let tokenResult: IdTokenResult | undefined;
        try {
          tokenResult = await auth.currentUser?.getIdTokenResult(
            options.forceTokenRefresh === true,
          );
        } catch (error) {
          this.loading = false;
          if (handleFirebaseAuthNetworkError(error, "fetchAndSetUser token refresh")) {
            return false;
          }

          throw error;
        }

        let response: AxiosResponse | undefined;
        try {
          response = await axios.get("/auth/me");
        } catch (error) {
          if (accountSessionVersion !== sessionVersion || auth.currentUser !== currentUser) {
            return false;
          }
          if (handleFirebaseAuthNetworkError(error, "fetchAndSetUser auth/me request")) {
            this.loading = false;
            return false;
          }

          const apiError = error as {
            status?: number;
            response?: { status?: number };
          } | null;
          const status = apiError?.status ?? apiError?.response?.status;
          const apiResponseData = getAuthApiErrorData(error);
          Sentry.logger.error("auth/me request failed", {
            status,
            hasResponseData: apiResponseData !== null,
            apiResponseData: getSentryAttributeValue(apiResponseData),
          });
          Sentry.captureException(new Error("auth/me request failed"), {
            tags: {
              area: "auth",
              action: "fetchAndSetUser",
              stage: "auth/me request",
            },
            extra: {
              status,
              apiResponseData,
            },
          });
          // Handle 401 unauthorized error
          if (status === 401) {
            this.loading = false;
            useSnackBarStore().toastError({
              message: i18n.global.t("errors.credentialsProblem"),
            });
            await this.logout();
            // Throw a specific error to signal 401 was handled
            throw new Error("UNAUTHORIZED_HANDLED");
          }
          // Re-throw other errors
          throw error;
        }

        const responseSummary = getAuthMeResponseSummary(response);
        Sentry.logger.info("auth/me response received", responseSummary);
        Sentry.addBreadcrumb({
          category: "auth.fetchAndSetUser",
          message: "/auth/me response received",
          level: "info",
          data: responseSummary,
        });

        try {
          if (response) {
            if (accountSessionVersion !== sessionVersion || auth.currentUser !== currentUser) {
              return false;
            }
            const { data } = response;
            if (data?.nextAccountId) {
              await this.changeSession({ accountId: data.nextAccountId });
              return false;
            }
            if (
              this.pendingAccountId &&
              data.currentAccount?.id !== this.pendingAccountId &&
              !data?.redirectToRegister
            ) {
              this.loading = false;
              return false;
            }
            if (data?.redirectToRegister) {
              this.loading = false;
              this.user = null;
              router.push({ name: "register", query: { step: 3 } });
              return false;
            }
            this.setUser(data?.user);
            this.security = data?.security || null;
            if (data.currentAccount?.id) {
              this.setAccountPlan(data.currentAccount.accountPlan || null);
              this.setAccountTimezone(data.currentAccount?.settings?.dashboard?.timezone || null);
            } else {
              this.loading = false;
              this.user = null;
              useSnackBarStore().toastError({
                message: i18n.global.t("errors.credentialsProblem"),
              });
              await this.logout();
              return false;
            }
            const activateIds = Object.keys(data.user?.accounts || {});
            if (!this.user?.accounts || !activateIds.includes(data.currentAccount.id)) {
              this.loading = false;
              this.user = null;
              useSnackBarStore().toastError({
                message: i18n.global.t("errors.credentialsProblem"),
              });
              await this.logout();
              return false;
            } else {
              this.userId = auth?.currentUser?.uid || "";
              // set current account in session storage for role and plan guard
              const account = {
                role: data?.user?.accounts?.[data?.currentAccount?.id]?.role,
                plan: data?.currentAccount?.accountPlan,
                accessStatus: data?.currentAccount?.nectarStatus,
              };
              setCurrentAccount(account);
              useAccountStore().setCurrentAccount(data.currentAccount);
              if (
                isMerchantSessionRoute(router.currentRoute.value) &&
                redirectMigratedAccount(data.currentAccount)
              ) {
                this.loading = false;
                return false;
              }
              setSentryAuthContext({
                userId: this.userId,
                currentAccountId: data.currentAccount.id,
                role: account.role,
                tokenResult,
              });
              // Initialize the listener
              this.listenToFavReaders(this.userId);
              this.listenToAccountPlan(data.currentAccount.id);
              this.listenToAccountSecurity(data.currentAccount.id);
            }
            this.loading = false;
            return true;
          }
          this.loading = false;
          return false;
        } catch (error) {
          Sentry.logger.error("auth/me response handling failed", responseSummary);
          Sentry.captureException(error, {
            tags: {
              area: "auth",
              action: "fetchAndSetUser",
              stage: "auth/me response handling",
            },
            extra: {
              responseSummary,
            },
          });
          throw error;
        }
      };

      if (auth.currentUser?.emailVerified) {
        try {
          return await doFetchMe();
        } catch (error) {
          // If 401 was already handled, just return
          if ((error as Error | null)?.message === "UNAUTHORIZED_HANDLED") {
            return false;
          }
          throw error;
        }
      } else {
        const checkForVerifiedInterval = setInterval(() => {
          auth.currentUser
            ?.reload()
            .then(async () => {
              if (auth.currentUser?.emailVerified) {
                this.verified = true;
                try {
                  await doFetchMe();
                } catch (error) {
                  // If 401 was already handled, just clear interval and return
                  if ((error as Error | null)?.message === "UNAUTHORIZED_HANDLED") {
                    clearInterval(checkForVerifiedInterval);
                    return;
                  }
                  throw error;
                }
                clearInterval(checkForVerifiedInterval);
              }
            })
            .catch((error) => {
              if (handleFirebaseAuthNetworkError(error, "email verification polling")) {
                return;
              }

              Sentry.captureException(error, {
                tags: {
                  area: "auth",
                  action: "emailVerificationPolling",
                },
              });
            });
        }, 2000);
        return false;
      }
    },
    async setUserId(id: string): Promise<void> {
      this.userId = id;
    },
    async updateUser(): Promise<void> {
      const navigator: CustomNavigator = window.navigator;
      const timezone = moment.tz.guess();
      const language = navigator.languages
        ? navigator.languages[0]
        : navigator.language || navigator.userLanguage;
      const payload = {
        timezone,
        language,
      };
      const response = await axios.patch("/auth/me", payload);
      this.user = response.data.user;
      useAccountStore().setCurrentAccount(response.data.currentAccount);
      // update current account in session storage for role and plan guard
      const account = {
        role: response?.data?.user?.accounts[response?.data?.currentAccount?.id]?.role,
        plan: response?.data?.currentAccount?.accountPlan,
        accessStatus: response?.data?.currentAccount?.nectarStatus,
      };
      setCurrentAccount(account);
    },
    async verifyEmailLink(): Promise<boolean> {
      const auth = getAuth();

      let res = false;
      if (auth.currentUser) {
        res = await sendEmailVerification(auth.currentUser)
          .then(() => {
            return true;
          })
          .catch((error: unknown) => {
            useSnackBarStore().toastError({ message: getErrorMessage(error) });
            return false;
          });
      }

      return res;
    },
    async register(payload: CreateUser): Promise<boolean> {
      const { displayName, email, password, token } = payload;
      try {
        await this.createAuthUser({
          displayName,
          email,
          password,
          token,
        });

        await this.fetchAndSetUser();
        return true;
      } catch {
        return false;
      }
    },
    async createAuthUser(payload: CreateUser): Promise<void> {
      const { email, password, displayName, token } = payload;
      const auth = getAuth();

      if (!password || password.length < 8) {
        useSnackBarStore().toastError({
          message: i18n.global.t("errors.password"),
        });
        return;
      }
      if (!displayName) {
        useSnackBarStore().toastError({
          message: i18n.global.t("errors.authName"),
        });
        return;
      }
      const onError = (error: unknown) => {
        this.loading = false;
        if (handleFirebaseAuthNetworkError(error, "createAuthUser")) {
          return;
        }

        const authError = error as { code?: string; message?: string } | null;
        if ("auth/email-already-in-use" === authError?.code) {
          useSnackBarStore().toastError({
            message: i18n.global.t("errors.emailExists"),
          });
          router.push({ name: "login", query: { email } });
          throw new Error();
        } else {
          useSnackBarStore().toastError({
            message: i18n.global.t(`errors.${authError?.message}`),
          });
        }
      };

      try {
        // 1. Create the Firebase Auth user
        await createUserWithEmailAndPassword(auth, email, password);

        if (!auth.currentUser) {
          return;
        }
        // 2. If user was created with a token, process the token before we fetch
        const tokenResult = token ? await this.processToken({ token }) : null;
        if (token && !tokenResult) return;

        if (token) {
          // Force Firebase Auth to pick up server-side emailVerified and custom claim changes from
          // the token processing call before any authenticated backend requests are made.
          const refreshed = await refreshCurrentFirebaseUser("register token session refresh");
          if (!refreshed) return;
        }

        // 3. Update the Firebase Auth user displayName value to their provided name
        await updateProfile(auth.currentUser, { displayName });

        // 4. Update the database user with browser preferences (timezone & language)
        await this.updateUser();

        // 5. Send email verification
        if (!token) this.verifyEmailLink();
        if (tokenResult && tokenResult.accountId) {
          await this.changeSession({ accountId: tokenResult.accountId });
        } else if (token) {
          await this.afterLogin({ forceTokenRefresh: true });
        }

        this.loading = false;
      } catch (e: unknown) {
        onError(e);
      }
    },
    async createAccount(data: CreateAccount): Promise<void> {
      const name = data.name;
      const country = data.country;
      try {
        const response: AxiosResponse = await axios.post("/account", {
          name,
          country,
        });

        useAccountStore().account = response.data;

        // Ensure next API calls include updated server-side custom claims.
        const refreshed = await refreshCurrentFirebaseUser("createAccount session refresh");
        if (!refreshed) {
          return;
        }
        await this.afterLogin();

        useSnackBarStore().toastSuccess({
          message: i18n.global.t("accounts.accountCreated"),
        });
        await router.push({ name: "dashboard" });
      } catch (error) {
        useSnackBarStore().toastError({
          message: getErrorMessage(error),
        });
      }
    },
    async changeSession(payload: ChangeSessionPayload): Promise<void> {
      const { accountId } = payload || {};
      this.changeSessionLoading = true;
      this.pendingAccountId = accountId;
      accountSessionVersion += 1;
      const sessionVersion = accountSessionVersion;

      // Update our session to prepare for SSO
      await axios
        .post("/auth/session", {
          account: accountId,
        })
        .then(async () => {
          const refreshed = await refreshCurrentFirebaseUser("changeSession session refresh");
          if (!refreshed || accountSessionVersion !== sessionVersion) {
            return;
          }
          await this.afterLogin({ forceTokenRefresh: true });
        })
        .catch((error: AxiosResponse) => {
          useSnackBarStore().toastError({ message: error.data });
        })
        .finally(() => {
          if (accountSessionVersion === sessionVersion) {
            this.changeSessionLoading = false;
            this.pendingAccountId = null;
          }
        });
    },
    async resetPassword(payload: { email: string }): Promise<boolean> {
      const { email } = payload;
      const auth = getAuth();

      return await sendPasswordResetEmail(auth, email)
        .then(() => {
          return true;
        })
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .catch((error: AxiosError) => {
          useSnackBarStore().toastError({
            message: i18n.global.t("page.resetPassword.error"),
          });
          return false;
        });
    },
    async confirmEmailVerification(oobCode: string): Promise<boolean> {
      const auth = getAuth();

      return await applyActionCode(auth, oobCode)
        .then(() => {
          return true;
        })
        .catch((error: unknown) => {
          useSnackBarStore().toastError({ message: getErrorMessage(error) });
          return false;
        });
    },

    async listenToFavReaders(userId: string): Promise<void> {
      if (!userId) {
        return;
      }

      if (activeUserUnsubscribe) {
        activeUserUnsubscribe();
        activeUserUnsubscribe = null;
      }

      const userRef = doc(db, "users", userId);
      activeUserUnsubscribe = onSnapshot(userRef, (doc) => {
        const user = doc.data() as
          | (FirebaseUser & { favoriteReaders?: Record<string, string[]> })
          | undefined;
        this.realTimeFavReaders = user?.favoriteReaders || {};

        const accountId = useAccountStore().account?.id;
        const assignedLocationIds = accountId
          ? user?.accounts?.[accountId]?.assignedLocationIds
          : undefined;

        if (!accountId || assignedLocationIds === undefined) {
          return;
        }

        if (!this.user?.accounts) {
          return;
        }

        const currentAccount = this.user.accounts[accountId];
        if (!currentAccount) {
          return;
        }

        this.user = {
          ...this.user,
          accounts: {
            ...this.user.accounts,
            [accountId]: {
              ...currentAccount,
              assignedLocationIds,
            },
          },
        };
      });
    },
    getFavTerminals(accountId: string) {
      return this.realTimeFavReaders[accountId];
    },

    listenToAccountSecurity(accountId: string): void {
      if (!accountId) {
        return;
      }

      if (activeAccountSecurityUnsubscribe) {
        activeAccountSecurityUnsubscribe();
        activeAccountSecurityUnsubscribe = null;
      }

      const accountRef = doc(db, "accounts", accountId);
      activeAccountSecurityUnsubscribe = onSnapshot(accountRef, (snapshot) => {
        const currentAccount = useAccountStore().account;
        if (
          this.logoutLoading ||
          this.changeSessionLoading ||
          currentAccount?.id !== accountId ||
          getAuth().currentUser?.uid !== this.userId
        ) {
          return;
        }
        const account = snapshot.data() as FirebaseAccountSnapshot | undefined;
        currentAccount.migratedToBeta = account?.migratedToBeta === true;
        if (currentAccount.migratedToBeta) {
          if (isMerchantSessionRoute(router.currentRoute.value)) {
            redirectMigratedAccount(currentAccount);
          }
          return;
        }
        const accountRequires2fa = account?.settings?.security?.require2fa === true;
        const existingSecurity = this.security || {
          mfaEnrolled: false,
          anyAccountRequires2fa: false,
          accountsRequiring2fa: [],
        };
        const existingAccountIds = existingSecurity.accountsRequiring2fa || [];
        const accountsRequiring2fa = accountRequires2fa
          ? Array.from(new Set([...existingAccountIds, accountId]))
          : existingAccountIds.filter((id) => id !== accountId);
        const { currentUser } = getAuth();
        const mfaEnrolled = !!currentUser && multiFactor(currentUser).enrolledFactors.length > 0;

        this.security = {
          mfaEnrolled,
          anyAccountRequires2fa: accountsRequiring2fa.length > 0,
          accountsRequiring2fa,
        };

        if (accountRequires2fa && !mfaEnrolled && router.currentRoute.value.name !== "mfaSetup") {
          router.push({ name: "mfaSetup" });
        }
      });
    },

    //subscribe to listen plan changes
    async listenToAccountPlan(accountId: string): Promise<void> {
      if (!accountId) {
        return;
      }

      activeAccountPlanUnsubscribe?.();
      const accountRef = doc(db, "accounts", accountId);

      activeAccountPlanUnsubscribe = onSnapshot(accountRef, (doc) => {
        if (
          this.logoutLoading ||
          this.changeSessionLoading ||
          useAccountStore().account?.id !== accountId
        ) {
          return;
        }
        const account = doc.data() as FirebaseAccountSnapshot | undefined;
        this.accountPlan = account?.subscription?.productName || AccountPlan.STARTER;
        const planCanceled = account?.subscription?.status === "canceled";
        const currentTimestamp = moment();
        const subscriptionEndTimestamp = moment.unix(
          account?.subscription?.current_period_end ?? 0,
        );

        if (planCanceled && subscriptionEndTimestamp.isBefore(currentTimestamp)) {
          this.accountPlan = AccountPlan.STARTER;
          const newAccount = {
            plan: AccountPlan.STARTER,
          };
          updateCurrentAccount(newAccount);
          return;
        }
      });
    },
  },
});
